/**
 * ACCOUNT
 */

import { formatDate } from "@/utils/format-date.utils"
import { Project } from "./project.types"
import { Subscription } from "./subscription.types"
import { Supervisor } from "./supervisor.types"
import { Template } from "./template.types"
import { User } from "./user.types"
import { CONFIG_AXIS_COLORS } from "./axis.types"
import type { Email } from "./email.types"
import {
  faQuestionCircle,
  faStar,
  faStarHalfAlt,
  faTimes,
  IconDefinition,
  faFaceFrown,
  faFaceLaugh,
  faSmile
} from "@fortawesome/free-solid-svg-icons"

//List of HRIS integrations supported by IDTree
export type AccountIntegration = "lucca" | "peoplesphere" | "entra"

//First choice for onboarding
//Track what action the user have done in the first place
export type AccountOnboardingAction = "project" | "templates" | "neither"

//Type of display for the results
//> note : mean of the score
//> satisfaction : ratio of positive answers vs ratio of negative answers
export type AccountScoreResult = "note" | "satisfaction"

//For a superadmin account define permission for every account
//> all : the children account have access to all templates
//> account : the children account have only access to the template of the superamdin account
//> public : the children account have only access to the topics of IDTree
//> list : the children account have access to a specific list of templates (defined in templateIds)
//> blocked : the children account can't access to any template
export type AccountTemplateRule = "all" | "account" | "public" | "list" | "blocked"

//Zone for an account (for better quickvote application availability)
export type AccountZone = "europe" | "america" | "asia"

//Type of customer success (for resell)
export const ACCOUNT_CS_TYPES = ["recurring", "semi_recurring", "oneshot"] as const
export type AccountCsType = (typeof ACCOUNT_CS_TYPES)[number]

//Mood for customer success (bad or good fit)
export const ACCOUNT_CS_MOODS = [
  { value: 1, icon: faFaceFrown },
  { value: 2, icon: faSmile },
  { value: 3, icon: faFaceLaugh }
]

//List of attributes that will be saved in sendInBlue when an account will be created in self-service
export class AccountAdsOptions {
  SOURCE: string | null = ""
  CAMPAIGNID: string | null = ""
  COMPANY_SIZE: string | null = ""
  ADGROUPID: string | null = ""
  KEYWORD: string | null = ""
  GCLID: string | null = ""
  MESSAGE: string | null = ""
  TARGETID: string | null = ""
  TRIAL: boolean = true
  TRIAL_SOURCE: string | null = ""
  TRIAL_PAGE: string | null = ""
  PERSONA: string | null = null
  constructor(accountAdsOptions: Partial<AccountAdsOptions> = {}) {
    Object.assign(this, accountAdsOptions)
  }
}

export type Tag = {
  color: string
  name: string
}

//Type of results for the heatmap
export type AccountHeatmapResult = "axes" | "topics" | "questions" | "survey"
export const ACCOUNT_RESULTS_TYPES: AccountHeatmapResult[] = ["axes", "topics", "questions"]

//Color palette
export class AccountColors {
  active: string = "#95d5df"
  brandPrimary: string = "#7d8ee0"
  brandSecondary: string = "#7d8ee0"
  brandText: string = "#FFFFFF"
  cta: string = "#1b759a"
  email: string = "#7d8ee0"
  tags: Tag[] = CONFIG_AXIS_COLORS.map((color: string) => ({ color, name: "" }))
  constructor(accountColors: Partial<AccountColors> = {}) {
    Object.assign(this, accountColors)
  }
}

//List of features of IDTree
export class AccountFeatures {
  survey: boolean = false //Launch survey in the company
  risks: boolean = false //Professional risks evaluation
  integration: boolean = false //Can use integration with HRIS
  constructor(item: Partial<AccountFeatures> = {}) {
    Object.assign(this, item)
  }
}

//List of modules for the survey feature
export class AccountModulesSurvey {
  qrcode: boolean = false //Can print invitations with QRCode
  sms: boolean = false //Can send invitations by SMS
  segment: boolean = false //Can send survey or axes to specific populations
  languages: boolean = false //Can use multiple languages
  learning: boolean = false //E-learning module
  nps: boolean = false //Net promoter score widget
  rps: boolean = false //Can display psychosocial hazards dashboards
  task: boolean = false //Action plans
  assistant: boolean = false //AI assistant
  populations: boolean = false //Results by population (flute de pan)
  summarize: boolean = false //Summarize messages
  translate: boolean = false //Automatic translation (DeepL)
  benchmark: boolean = false //Benchmark questions & heatmap
  projectAssistant: boolean = false //AI checks before launching a project (distinct from assistant)
  inequalities: boolean = false //Dispersion of the scores inside a population (internal : only seen by the people connected as supervisor, never listed to the client)
  constructor(item: Partial<AccountModulesSurvey> = {}) {
    Object.assign(this, item)
  }
}

//List of modules for the risks feature
export class AccountModulesRisks {
  scales: boolean = false //Access to scales & attributes configuration
  constructor(item: Partial<AccountModulesRisks> = {}) {
    Object.assign(this, item)
  }
}

//Premium modules that can be activated
export class AccountModules {
  survey: AccountModulesSurvey = new AccountModulesSurvey()
  risks: AccountModulesRisks = new AccountModulesRisks()
  constructor(item: Partial<AccountModules> = {}) {
    Object.assign(this, item)
  }
}

//List of options that can be specific for an account
//Features an observer can be denied, by the account and in addition by a role
export type ObserverOptions = Pick<
  AccountOptions,
  | "dashboardTabsHiddenForObserver"
  | "hideSummarizeForObserver"
  | "hideAssistantForObserver"
  | "hideReportForObserver"
  | "hiddenFiltersForObserver"
  | "actionPlanAccessForObserver"
  | "showAccountCompareForObserver"
  | "heatmapFilterNameForObserver"
>

//Filters hidden for the observers, stored as names separated by commas (an old list is read too)
export function getHiddenFilterNames(hiddenFiltersForObserver?: string | string[]): string[] {
  return String(hiddenFiltersForObserver ?? "")
    .split(",")
    .map((name) => name.trim())
    .filter(Boolean)
}

//Action plans an observer reaches: none, his own, those of his scope read only or editable
export const ACTION_PLAN_ACCESS = ["none", "self", "scopeRead", "scopeWrite"] as const
export type ActionPlanAccess = (typeof ACTION_PLAN_ACCESS)[number]

export class AccountOptions {
  roleManagementEnabled: boolean = false //Roles replace the admin/observer/manager assignment
  managerFilters: string[] = [] //Manager attributes, from the highest level to the direct manager (role "Managers")
  allowAssistant: boolean = false //Allow use of AI for advices
  allowObserverSettings: boolean = false //Observers can edit their view
  ceilsBirthDate: number[] = [35, 50] //Values in years for ages populations
  ceilsCompanyWelcomeDate: number[] = [24, 120] //Values in months for seniority
  ceilsTesters: number = 5 //Maxium of testeurs for a test survey
  ceilsRepartition: number[] = [6, 8] //Ceils of satisfaction for the repartition widget
  ceilsRepartitionGollac: number[] = [5] //Ceils for the repartition widget of a Gollac survey (also drives the exposed employees ceil of the RPS Gollac dashboard)
  ceilsRepartitionCustom?: boolean //Use the custom scales instead of the default ceils and labels. Undefined : never set, deduced from ceilsRepartition (cf. get-repartition-ceils.utils)
  ceilsRepartitionLabels: string[] = [] //Custom labels of the repartition groups (one per group, empty string : keep the translated label)
  ceilsRepartitionGollacLabels: string[] = [] //Same for a Gollac survey
  customEmailSenderInstance: boolean = false //Uses custom instance for sending email (instances are configured in the mail.utils file)
  dashboardDisplayMode: AccountScoreResult = "note" //show note /10 or % of satisfaction
  dashboardHistoryScale: number[] = [-1, -0.1, 0.1, 1] //Scale for the colors for history evolution (from negative to positive)
  dashboardNightmareModeHeatmap: boolean = true //Display custom display for the heatmap
  dashboardNightmareModeDisplay: boolean = false //Display custom colors for the tree
  dashboardNightmareModeMax: number = 70 //Ceil max for the nighmare mode (note > 80 will be in specific color)
  dashboardNightmareModeMin: number = 49 //Ceil min for the nighmare mode (note < 50 will be in specific color)
  dashboardNightmareColor1: string = "#eb5a46" //Display color for topics where note < dashboardNightmareModeMin
  dashboardNightmareColor2: string = "#f2b100" //Display color for topics where dashboardNightmareModeMin < note < dashboardNightmareModeMax
  dashboardNightmareColor3: string = "#4FD683" //Display color for topics where note > dashboardNightmareModeMax
  dashboardTabsHiddenForObserver: string[] = [] //List of tabs hidden for the observer
  defaultIcon: string = "institutions/shield" //Default icon for multiple account
  disableChat: boolean = false //Disable chat information
  emailDisplayCode: boolean = false //Show QV code in the invitation mail
  filterOrder: string[] = [] //Order to display filters in dashboard (for supervisor)
  gollacFilterName: null | string = null //For heatmap gollac => default filter name not specified
  gollacManagerAttributeName: null | string = null //For heatmap gollac => default filter attribute not specified
  gollacHeatmapViewDefault: AccountHeatmapResult = "topics" //For heatmap gollac => default heatmap view
  ceilsGollacMinValue: number = 0.4 //For heatmap gollac => default ceils
  ceilsGollacMaxValue: number = 1.1 //For heatmap gollac => default ceils
  historyAliases: Record<string, string[]> = {} //History : current question label => labels of the same question in older surveys
  heatmapFilterName: null | string = null //For heatmap => default filter name specified
  heatmapDisplayCompany: boolean = true //For heatmap => display company column by default
  heatmapDisplayParticipation: boolean = true //For heatmap => display participation row by default
  heatmapDisplayNoScore: boolean = true //For heatmap => hide columns without results
  heatmapResultsType: AccountHeatmapResult | null = null //For heatmap => type of results expected by default (axes, questions...)
  hiddenFiltersForObserver: string = "" //Filters hidden for observer, names separated by commas
  actionPlanAccessForObserver: ActionPlanAccess = "self" //Replaced by the role's choice
  showAccountCompareForObserver: boolean = false //Matrix: column of the whole account next to the scope (account or role)
  heatmapFilterNameForObserver: null | string = null //Default filter of the observers' dashboards, replaced by the role's choice
  isObserverNotRestricted: boolean = false
  integration: null | AccountIntegration = null //Integration with HRIS options
  image: boolean = false //Is a picture uploaded for the account
  importMapping: Record<string, string | null> | null = null //Column name => user field chosen at the last import (null : column left unmapped). Set : the guesses are off
  lastUpload: Date | null = null //Date of the last excel upload
  observersFilterName: string | null = null //Use a specific filter for naming the observers
  observersSeeAllResults: boolean = false //Allow observer to access the result for the whole company
  onboardingAction: AccountOnboardingAction | null = null //Onboarding first action
  sector: string | null = null //Sector for the account
  sso: boolean = false //Connexion with SSO
  ssoOrganization: string = "" //Identifier of the sso organization (in cryptr)
  topicRepartitionCeil: number = 50 //Ceil for positive vote for topic repartition
  hideAssistantForObserver: boolean = false // Hide the assistant module
  hideSummarizeForObserver: boolean = false //Hide the summarize module
  hideNoAnswers: boolean = false //Don't show count of no answer by questions
  dashboardNeutralSeparated: boolean = false //Show the neutral answers apart (grey) in the gauge of a topic instead of counting them as positive
  hideStartedForParticipation: boolean = true //Don't show the "started in the dashboard"
  hideReportForObserver: boolean = false //Hide report features for observer
  genderShowFilter: boolean = true //display gender filter in dashboard
  birthDateShowFilter: boolean = true //display birth date filter in dashboard
  companyWelcomeDateShowFilter: boolean = true //display company welcome date filter in dashboard
  zone: AccountZone = "europe" //Change zone (for different quickvote link each bond to specific server and region)
  riskDisablePublicLink: boolean = false //Do not allow public link to be share
  demoTemplate: { accountId: string; accountName: string } | null = null //Saved template account for demo generation
  isDemoAccount: boolean = false //Account is a demo account (visible to all supervisors)
  communicationEmail: Email | null = null //Email used for account-wide communication
  communicationEmailTesters: string[] = [] //Test email addresses for account-wide communication

  constructor(item: Partial<AccountOptions> = {}) {
    Object.assign(this, item)
  }
}

/**
 * ACCOUNT
 */
export class Account {
  allowedTemplates: string[] = [] //List of templates allowed
  colors: any = new AccountColors()
  csMood: number | null = null
  csType: AccountCsType | null = null
  dateStart: Date = new Date()
  features: AccountFeatures = new AccountFeatures() //All features available
  id: string = ""
  invitations: number = 0 //List of invitations for a survey
  isPasswordRequired: boolean = false //A password is required for observer and manager
  isTrial: boolean = false //Is free account with limited features
  isSuperAccount: boolean = false //The account can manage other accounts (consultive firm with many customers or big company)
  lastSubscriptionEnd: number | null = null
  modules: AccountModules = new AccountModules()
  name: string = ""
  options: AccountOptions = new AccountOptions()
  participationMin: number = 5
  superAccountId: string | null = null
  templateRule: AccountTemplateRule = "all" //Template rule
  Projects: Project[] = []
  Subscriptions: Subscription[] = []
  SupervisorId: string | null = null
  SupervisorIdSales: string | null = null
  Supervisor: Supervisor | null = null
  SupervisorSales: Supervisor | null = null
  Templates: Template[] = []
  Users: User[] = []

  constructor(account: Partial<Account> = {}) {
    if (!account) account = new Account()

    account.options = new AccountOptions(account.options)
    account.colors = new AccountColors(account.colors)
    account.Subscriptions = account.Subscriptions ? account.Subscriptions.map((x) => new Subscription(x)) : []
    if (account.Supervisor) account.Supervisor = new Supervisor(account.Supervisor)
    if (account.SupervisorSales) account.SupervisorSales = new Supervisor(account.SupervisorSales)
    account.Projects = account.Projects ? account.Projects.map((x) => new Project(x)) : []
    account.Templates = account.Templates ? account.Templates.map((x) => new Template(x)) : []
    account.modules = account.modules ? new AccountModules(account.modules) : new AccountModules()

    Object.assign(this, account)
  }

  //Get date formated
  get dateStartLabel(): string {
    return formatDate(this.dateStart, true, false)
  }

  //Get status of survey for account
  //If launch or program display message
  get projectStatus(): string {
    if (this.Projects.length) {
      return this.Projects[0].statusLabel
    } else {
      return ""
    }
  }

  get csTypeColor(): string | null {
    switch (this.csType) {
      case "recurring":
        return "#1CB06E"
      case "semi_recurring":
        return "#ff9500"
      case "oneshot":
        return "#eb5a46"
      default:
        return null
    }
  }

  get csTypeIcon(): IconDefinition {
    switch (this.csType) {
      case "recurring":
        return faStar
      case "semi_recurring":
        return faStarHalfAlt
      case "oneshot":
        return faTimes
      default:
        return faQuestionCircle
    }
  }
}

//Smartlink Account
export const ACCOUNT_DEMO = new Account({
  id: "1dbad5e4-93d4-4d85-871a-21dc9a17a0ad", //Smartlink
  name: "SmartLink"
})

//Color for the demo accounts
export const ACCOUNT_DEMO_COLOR = "#6c547b"

//Smartlink Account
export interface AccountState {
  active: Account //Compte sélectionné
  ascOrDesc: "asc" | "desc" //order to display accounts (asc or desc)
  count: number //Nombre de comptes total
  countSearch: number //Nombre de comptes recherchés
  list: Account[] //Liste de comptes
  order: string //order too display accounts (field name)
  status: string //Statut en cours
}

export const ACCOUNT_ACTIVATE: string = "ACCOUNT_ACTIVATE"
export const ACCOUNT_ADD: string = "ACCOUNT_ADD"
export const ACCOUNT_EDIT: string = "ACCOUNT_EDIT"
export const ACCOUNT_GET: string = "ACCOUNT_GET_ALL"
export const ACCOUNT_GET_COUNT: string = "ACCOUNT_GET_COUNT"
export const ACCOUNT_GET_COUNT_SEARCH: string = "ACCOUNT_GET_COUNT_SEARCH"
export const ACCOUNT_INIT: string = "ACCOUNT_INIT"
export const ACCOUNT_ORDER: string = "ACCOUNT_ORDER"
export const ACCOUNT_REMOVE: string = "ACCOUNT_REMOVE"
export const ACCOUNT_STATUS: string = "ACCOUNT_SET_STATUS"
